/**
 * Baked GLB first paint. Runtime HDR + textures preload in background; swatch switch loads only if not ready.
 */
window.ProductModelTexture = {
  lastAppliedKey: "",
  runtimeTextureApplied: false,
  textureCache: new Map(),
  textureReadyKeys: new Set(),
  hdrPrefetched: false,
  hdrPrefetchPromise: null,
  modelViewer: null,
  ready: false,
  preloadStarted: false,

  normalize(value) {
    return (value || "").trim().toLowerCase();
  },

  getRoughness(textureValue) {
    const value = this.normalize(textureValue);
    if (value.includes("matte")) return 0.88;
    if (value.includes("gloss") || value.includes("glossy")) return 0.12;
    return 0.4;
  },

  getModelViewer() {
    if (this.modelViewer?.isConnected) {
      return this.modelViewer;
    }

    const viewer =
      document.querySelector(".product-media-main.is-active model-viewer") ||
      document.querySelector("product-model[loaded] model-viewer");

    if (viewer) {
      this.modelViewer = viewer;
    }
    return viewer;
  },

  getProductModel() {
    return this.getModelViewer()?.closest("product-model") || document.querySelector(".product-media-main product-model");
  },

  getDefaultTextureValue() {
    return document.querySelector("product-texture-picker")?.dataset?.defaultValue || "Twill Weave Glossy";
  },

  isDefaultBakedSelection(textureValue) {
    return this.normalize(textureValue) === this.normalize(this.getDefaultTextureValue());
  },

  getTextureUrl(imageUrl) {
    if (!imageUrl) return "";

    try {
      const url = new URL(imageUrl, window.location.origin);
      if (url.hostname.includes("cdn.shopify.com")) {
        const version = document.querySelector("product-texture-picker")?.dataset?.textureCacheVersion || "5";
        url.searchParams.set("cb", version);
        url.searchParams.set("width", "1024");
      }
      return url.toString();
    } catch {
      return imageUrl;
    }
  },

  applyRuntimePbr(modelViewer, textureValue) {
    const materials = modelViewer?.model?.materials;
    if (!materials?.length) return;

    const roughness = this.getRoughness(textureValue);
    materials.forEach((material) => {
      const pbr = material.pbrMetallicRoughness;
      if (!pbr) return;
      pbr.setBaseColorFactor([1, 1, 1, 1]);
      pbr.setMetallicFactor(0.25);
      pbr.setRoughnessFactor(roughness);
    });
  },

  prefetchEnvironment(productModel) {
    if (this.hdrPrefetchPromise) {
      return this.hdrPrefetchPromise;
    }

    const { image } = window.ProductModel?.getRuntimeEnvironmentConfig(productModel) || {};
    if (!image) {
      this.hdrPrefetched = true;
      this.hdrPrefetchPromise = Promise.resolve();
      return this.hdrPrefetchPromise;
    }

    this.hdrPrefetchPromise = fetch(image, { mode: "cors" })
      .then(() => {
        this.hdrPrefetched = true;
      })
      .catch(() => {
        this.hdrPrefetched = false;
      });

    return this.hdrPrefetchPromise;
  },

  isSwatchReady(textureUrl) {
    return Boolean(textureUrl && this.textureReadyKeys.has(textureUrl));
  },

  async waitForViewerReady(modelViewer) {
    if (!modelViewer) return null;

    if (!modelViewer.loaded) {
      await new Promise((resolve) => {
        modelViewer.addEventListener("load", resolve, { once: true });
      });
    }

    return modelViewer;
  },

  async loadImageDataUrl(imageUrl) {
    const textureUrl = this.getTextureUrl(imageUrl);
    const image = new Image();
    image.crossOrigin = "anonymous";

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = textureUrl;
    });

    const maxSize = 1024;
    const scale = Math.min(1, maxSize / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  },

  async getOrCreateModelTexture(modelViewer, textureUrl) {
    if (this.textureReadyKeys.has(textureUrl)) {
      return this.textureCache.get(textureUrl);
    }

    if (this.textureCache.has(textureUrl)) {
      return this.textureCache.get(textureUrl);
    }

    const pending = (async () => {
      const dataUrl = await this.loadImageDataUrl(textureUrl);
      const texture = await modelViewer.createTexture(dataUrl);
      this.textureReadyKeys.add(textureUrl);
      return texture;
    })();

    this.textureCache.set(textureUrl, pending);
    return pending;
  },

  async preloadSwatchTextures(modelViewer, picker) {
    const defaultValue = picker?.dataset?.defaultValue;
    const urls = (picker?.options || [])
      .filter((option) => this.normalize(option.value) !== this.normalize(defaultValue))
      .map((option) => this.getTextureUrl(option.image))
      .filter(Boolean);

    await Promise.allSettled(urls.map((textureUrl) => this.getOrCreateModelTexture(modelViewer, textureUrl)));
  },

  async ensureSwatchReady(modelViewer, productModel, textureUrl) {
    await this.waitForViewerReady(modelViewer);
    await this.prefetchEnvironment(productModel);
    await this.getOrCreateModelTexture(modelViewer, textureUrl);
    return this.hdrPrefetched && this.isSwatchReady(textureUrl);
  },

  async applyTexture(imageUrl, textureValue) {
    if (!imageUrl || !textureValue) return false;

    if (!this.runtimeTextureApplied && this.isDefaultBakedSelection(textureValue)) {
      return true;
    }

    const textureUrl = this.getTextureUrl(imageUrl);
    const applyKey = `${textureValue}::${textureUrl}`;
    if (this.lastAppliedKey === applyKey) return true;

    const modelViewer = this.getModelViewer();
    const productModel = modelViewer?.closest("product-model") || this.getProductModel();
    if (!modelViewer || !productModel) return false;

    const readyNow = this.hdrPrefetched && this.isSwatchReady(textureUrl);
    if (!readyNow) {
      window.ProductModel?.showSwatchLoading(productModel);
    }

    try {
      await this.ensureSwatchReady(modelViewer, productModel, textureUrl);

      window.ProductModel?.applyRuntimeEnvironment(modelViewer, productModel);
      this.applyRuntimePbr(modelViewer, textureValue);

      const texture = await this.getOrCreateModelTexture(modelViewer, textureUrl);
      const materials = modelViewer.model?.materials || [];
      let applied = false;

      for (const material of materials) {
        const slot = material.pbrMetallicRoughness?.baseColorTexture;
        if (!slot?.setTexture) continue;
        await slot.setTexture(texture);
        applied = true;
      }

      if (applied) {
        this.runtimeTextureApplied = true;
        this.lastAppliedKey = applyKey;
      }

      return applied;
    } catch (error) {
      console.warn("[ProductModelTexture] Swatch apply failed:", error);
      return false;
    } finally {
      window.ProductModel?.hideSwatchLoading(productModel);
    }
  },

  onModelReady(modelViewer) {
    if (modelViewer) {
      this.modelViewer = modelViewer;
    }

    if (this.ready) return;

    this.ready = true;
    this.scheduleRuntimePreload(modelViewer);
  },

  scheduleRuntimePreload(modelViewer) {
    if (this.preloadStarted) return;
    this.preloadStarted = true;

    const run = async () => {
      const picker = document.querySelector("product-texture-picker");
      const productModel = modelViewer?.closest("product-model") || this.getProductModel();
      if (!modelViewer || !picker) return;

      await this.prefetchEnvironment(productModel);
      await this.preloadSwatchTextures(modelViewer, picker);
    };

    window.requestIdleCallback?.(run, { timeout: 2000 }) || window.setTimeout(run, 300);
  },

  init() {
    document.addEventListener("product:model-ready", (event) => {
      if (event.detail?.modelViewer) {
        this.modelViewer = event.detail.modelViewer;
      }
      this.onModelReady(event.detail?.modelViewer);
    });
  },
};

window.addEventListener("DOMContentLoaded", () => {
  window.ProductModelTexture.init();
});
